import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

DialogAddIdentifierView.propTypes = {
  item: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogAddIdentifierView({ item, onCancel, onDone }) {
  const editMode = !!item;
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [code, setCode] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const [partnerList, setPartnerList] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const partnerOptions = useMemo(
    () =>
      (partnerList ?? []).map((partner) => ({
        vehicleIdentityPartnerId: partner?.vehicleIdentityPartnerId?.toString(),
        name: partner?.name,
      })),
    [partnerList]
  );

  const dropdownVehicleEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="vehicleId_dialog"
        name="vehicleId_dialog"
        ignoreConfirmClick={true}
        toggleElement={
          <button
            className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            type="button"
          >
            {selectedVehicle?.name ?? 'Chọn xe'}
          </button>
        }
        selectedValue={selectedVehicle}
        dropdownMenuItems={[{ name: 'Xe', role: 'VEHICLE' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.Vehicle.requestGetVehicleList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
            autoCharge: "false", // Chỉ lấy xe không có mã định danh để tránh nhầm lẫn khi chọn
          }).then((data) => ({
            values: data.vehicles?.map((row) => ({
              id: row?.vehicleId,
              name: row?.plate,
              item: row,
            })),
            total: data.total,
          }));
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedVehicle(value);
          setVehicleId(value?.id ?? value?.item?.vehicleId ?? null);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        defaultValue={{ name: 'Xe', role: 'VEHICLE' }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{ borderTop: `1px solid ${AppColors.gray[300]}` }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedVehicle(null);
              setVehicleId(null);
            })}
          </div>
        }
      />
    );
  }, [selectedVehicle]);

  useEffect(() => {
    ChargeHelper.API.requestGetChargeIdentityPartnerList({
      page: 0,
      limit: 10,
    }).then((response) => {
      setPartnerList(response?.partners ?? []);
    });
  }, []);

  useEffect(() => {
    setCode(item?.code ?? '');

    if (item?.vehicleId) {
      setSelectedVehicle({ id: item.vehicleId, name: item.vehicle?.plate ?? item.vehicleId });
      setVehicleId(item.vehicleId);
    } else {
      setSelectedVehicle(null);
      setVehicleId(null);
    }
  }, [item]);

  useEffect(() => {
    if (item?.vehicleIdentityPartnerId) {
      const partner = _.find(
        partnerOptions,
        (row) =>
          row.vehicleIdentityPartnerId?.toString() ===
          item.vehicleIdentityPartnerId?.toString()
      );
      setSelectedOperator(
        partner
          ? {
            vehicleIdentityPartnerId: partner.vehicleIdentityPartnerId,
            name: partner.name,
          }
          : {
            vehicleIdentityPartnerId: item.vehicleIdentityPartnerId?.toString(),
            name: item.vehicleIdentityPartnerId,
          }
      );
      return;
    }

    if (partnerOptions.length > 0) {
      setSelectedOperator({
        vehicleIdentityPartnerId: partnerOptions[0].vehicleIdentityPartnerId,
        name: partnerOptions[0].name,
      });
    } else {
      setSelectedOperator(null);
    }
  }, [item, partnerOptions]);

  const isOperatorValid = !!selectedOperator?.vehicleIdentityPartnerId;

  const handleSubmit = async () => {
    setReadyToValidate(true);
    if (!Utils.isValid(code?.trim())) {
      ToastHelper.showError('Vui lòng nhập mã MAC/EVCC');
      return;
    }
    if (!isOperatorValid) {
      ToastHelper.showError('Vui lòng chọn đối tác sạc');
      return;
    }
    const params = {
      code: code.trim(),
      status: item?.status ?? ChargeConstants.ChargeIdentifierStatus.ACTIVE,
      vehicleId: vehicleId,
      vehicleIdentityPartnerId: _.toNumber(selectedOperator.vehicleIdentityPartnerId),
    };
    setLoading(true);
    try {
      let res;
      if (editMode) {
        res = await ChargeHelper.API.Identifier.requestUpdateIdentifier(item.vehicleIdentityId, params);
        if (res === true) {
          ToastHelper.showSuccess('Cập nhật mã định danh thành công');
          if (onDone) onDone();
        } else {
          ToastHelper.showError(_.isString(res) ? res : 'Cập nhật mã định danh thất bại');
        }
      } else {
        res = await ChargeHelper.API.Identifier.requestAddIdentifier(params);
        if (res === true) {
          ToastHelper.showSuccess('Thêm mã định danh thành công');
          if (onDone) onDone();
        } else {
          ToastHelper.showError(_.isString(res) ? res : 'Thêm mã định danh thất bại');
        }
      }
    } catch {
      ToastHelper.showError('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex flex-column gap-0 px-4 mt-4">
        <div className="mb-4">
          {UIHelper.buildFormLabel('Mã MAC/EVCC', true)}
          <KTFormInput
            type={KTFormInputType.text}
            placeholder="Nhập mã MAC/EVCC"
            name="code"
            value={code}
            onChange={(value) => setCode(value)}
            enableCheckValid={readyToValidate}
            isValid={Utils.isValid(code?.trim())}
            isTouched={readyToValidate}
            feedbackText={!Utils.isValid(code?.trim()) ? 'Vui lòng nhập mã MAC/EVCC' : ''}
            disabled={editMode}
          />
        </div>

        <div className="mb-4">
          {UIHelper.buildFormLabel('Đối tác sạc', true)}
          <KTBSDropdown
            name="vehicleIdentityPartnerId"
            toggleElement={
              <button
                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between${readyToValidate && !isOperatorValid ? ' border-danger' : ''}`}
                type="button"
              >
                {selectedOperator?.name ?? 'Chọn đối tác sạc'}
              </button>
            }
            selectedValue={selectedOperator?.vehicleIdentityPartnerId}
            dropdownMenuItems={partnerOptions.map((op) => ({
              name: op.name,
              value: op.vehicleIdentityPartnerId,
            }))}
            dropdownMenuClassName="overflow-auto w-100"
            dropdownMenuStyle={{ maxHeight: '30vh' }}
            onChange={(value) => {
              const op = _.find(
                partnerOptions,
                (o) => o.vehicleIdentityPartnerId?.toString() === value?.toString()
              );
              setSelectedOperator(
                op
                  ? {
                    vehicleIdentityPartnerId: op.vehicleIdentityPartnerId,
                    name: op.name,
                  }
                  : null
              );
            }}
            isDropdownForm={true}
          />
          {readyToValidate && !isOperatorValid && (
            <span className="text-danger" style={{ fontSize: '0.875rem' }}>Vui lòng chọn đối tác sạc</span>
          )}
        </div>

        <div className="mb-4">
          {UIHelper.buildFormLabel('Chọn xe')}
          {dropdownVehicleEl}
        </div>
      </div>
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
        {UIHelper.buildGreenButton(
          !editMode ? 'Thêm' : 'Cập nhật',
          handleSubmit,
          null,
          false,
          loading
        )}
      </div>
    </div>
  );
}

export default DialogAddIdentifierView;
